import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '../LocaleLink';
import { stripLangPrefix } from '../../utils/languages';
import {
  Menu, X, ChevronDown,
  Moon, Sun, Globe,
} from 'lucide-react';
import { TOP_NAV, isGroupHeading } from '../../data/topNav';
import { useDarkMode } from '../../utils/useDarkMode';
import LanguageModal, { useCurrentLangMeta } from './LanguageModal';
import LogoLockup from '../brand/LogoLockup';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useLang } from '../../utils/i18n';
import { localizedRouteLabel, navGroupLabel, topNavLabel } from '../../utils/chromeLabels';

const SWIPE_THRESHOLD = 50; // px — minimum rightward swipe to close

/**
 * Mobile slide-in drawer navigation (<1024 px).
 *
 * Full-height right-side drawer: primary CTA on top, then the shared TOP_NAV
 * items (direct links and collapsible accordions), plus a utility row at the
 * bottom.
 */
const MobileDrawer: React.FC = () => {
  const [open, setOpen]         = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { pathname }            = useLocation();
  const barePath                = stripLangPrefix(pathname);
  const { isDark, toggle: toggleDark } = useDarkMode();
  const currentLang             = useCurrentLangMeta();
  const { lang, t }             = useLang();

  const drawerRef    = useFocusTrap(open);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const touchStart   = useRef<{ x: number; y: number } | null>(null);

  // ── Close on route change ──
  useEffect(() => { setOpen(false); }, [pathname]);

  // ── Escape key ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Lock body scroll when drawer is open ──
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Keep the off-canvas links out of the tab order and accessibility tree.
  // React 18's typings predate the inert JSX property, so set the native
  // attribute directly on the drawer element.
  useEffect(() => {
    drawerRef.current?.toggleAttribute('inert', !open);
  }, [open, drawerRef]);

  // ── Swipe-right to close ──
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = Math.abs(touch.clientY - touchStart.current.y);
    // Only close on a predominantly horizontal rightward swipe
    if (dx > SWIPE_THRESHOLD && dx > dy) {
      setOpen(false);
    }
    touchStart.current = null;
  }, []);

  const closeDrawer = useCallback(() => setOpen(false), []);

  const rowBase = 'flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors';
  const rowIdle = 'text-gray-400 hover:bg-gray-800 hover:text-white';
  const rowActive = 'bg-violet-600/20 text-violet-300 font-semibold';

  return (
    <>
      {/* ── Mobile header bar ── */}
      <header className="bg-gray-950 border-b border-gray-800/80 sticky top-0 z-30 shadow-xl">
        <div className="w-full px-3">
          <div className="flex items-center h-12 gap-2">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center flex-shrink-0"
              aria-label={`TheNameWheel — ${t('nav.home')}`}
            >
              <LogoLockup />
            </Link>

            <div className="flex-1" />

            {/* Hamburger */}
            <button
              ref={hamburgerRef}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-controls="mobile-drawer"
              aria-label={t('chrome.menu')}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Backdrop ── */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* ── Drawer ── */}
      <div
        ref={drawerRef}
        id="mobile-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={t('chrome.mainNavigation')}
        aria-hidden={!open}
        tabIndex={-1}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={`fixed top-0 right-0 z-50 h-screen w-[300px] max-w-[85vw] bg-gray-950 border-l border-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out outline-none ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="font-bold text-white text-sm">{t('chrome.menu')}</span>
          <button
            onClick={closeDrawer}
            aria-label={t('wheel.close')}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto py-3 px-3">
          {/* Primary CTA */}
          <a
            href="https://discord.gg/FKjedfPxTf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-4 py-3 mb-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-bold shadow-sm shadow-[#5865F2]/50 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
            </svg>
            Discord
          </a>

          <nav aria-label={t('chrome.mainNavigation')} className="space-y-0.5">
            {TOP_NAV.map(item => {
              if (item.to) {
                return (
                  <Link
                    key={item.id}
                    to={item.to}
                    onClick={closeDrawer}
                    aria-current={barePath === item.to ? 'page' : undefined}
                    className={`${rowBase} ${barePath === item.to ? rowActive : rowIdle}`}
                  >
                    {topNavLabel(item.id, item.label, t)}
                  </Link>
                );
              }

              const hasActive = !!item.dropdown?.some(
                e => !isGroupHeading(e) && barePath === e.to.split('?')[0],
              );

              return (
                <details key={item.id} className="group" open={hasActive || undefined}>
                  <summary className={`flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl cursor-pointer list-none text-sm font-medium transition-colors ${hasActive ? 'text-violet-300' : rowIdle}`}>
                    {topNavLabel(item.id, item.label, t)}
                    <ChevronDown size={13} className="transition-transform group-open:rotate-180" aria-hidden="true" />
                  </summary>
                  <div className="mt-1 mb-1 ps-2 space-y-0.5">
                    {item.dropdown?.map(entry => {
                      if (isGroupHeading(entry)) {
                        return (
                          <p key={`group-${entry.group}`} className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                            {navGroupLabel(entry.group, t)}
                          </p>
                        );
                      }
                      const active = barePath === entry.to.split('?')[0];
                      return (
                        <Link
                          key={entry.to}
                          to={entry.to}
                          onClick={closeDrawer}
                          aria-current={active ? 'page' : undefined}
                          className={`${rowBase} ${active ? rowActive : rowIdle}`}
                        >
                          {entry.emoji && <span className="w-4 text-center" aria-hidden="true">{entry.emoji}</span>}
                          {localizedRouteLabel(lang, entry.to, entry.label)}
                        </Link>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </nav>
        </div>

        {/* ── Utility row at bottom ── */}
        <div className="border-t border-gray-800 px-4 py-3 flex items-center gap-2">
          {/* Dark mode toggle */}
          <button
            onClick={toggleDark}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
            aria-label={isDark ? t('chrome.light') : t('chrome.dark')}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            <span>{isDark ? t('chrome.light') : t('chrome.dark')}</span>
          </button>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-800" aria-hidden="true" />

          {/* Language selector */}
          <button
            onClick={() => setLangOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
            aria-label={t('chrome.language')}
          >
            <Globe size={16} />
            <span>{currentLang.flag} <span className="uppercase">{currentLang.code}</span></span>
          </button>
        </div>
      </div>

      {/* Language modal */}
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
    </>
  );
};

export default MobileDrawer;
